import React, { useEffect, useState, useCallback, useMemo } from "react";
import CustomButton from "../../components/Elements/Buttons/CustomButtons";
import TextStyles from "../../components/Elements/TextStyles/TextStyles";
import { useNavigate } from "react-router-dom";
import { SearchBar } from "../../components/Elements/Search/SearchBar";
import { Tables } from "../../components/Elements/Tables/Tables";
import { ReactComponent as Back } from "../../assets/svgs/back-button.svg";
import { ReactComponent as DeleteIcon } from "../../assets/svgs/delete.svg";
import { ReactComponent as EditIcon } from "../../assets/svgs/greenEdit.svg";
import CustomSwitch from "../../components/Elements/CustomSwitch/CustomSwitch";
import { API_URLS } from "../../api/apiUrls";
import { getApi, deleteApi, putApi } from "../../api/api_instance";
import CreateProjectTypeDialog from "./DialogBoxes/CreateProjectType";
import EditProjectTypeDialog from "./DialogBoxes/EditProjectType";
import SortData from "../../components/Elements/SortData/SortData";
import PopupCard from "../../components/Cards/PopupCard/PopupCard";

const ProjectType = () => {
  const navigate = useNavigate();
  const [projectTypes, setProjectTypes] = useState([]);
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedProjectType, setSelectedProjectType] = useState(null);
  const [pageNo, setPageNo] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 5;
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingProjectTypeIds, setLoadingProjectTypeIds] = useState([]);
  const [popup, setPopup] = useState({
    visible: false,
    type: "", // "success" | "error" | "warning"
    title: "",
    message: "",
  });

  const headers = useMemo(
    () => [
      { label: "Sr. No.", key: "serial" },
      { label: "Project Services", key: "project_type" },
      { label: "Description", key: "description" },
      { label: "Service Cost (₹)", key: "service_cost" },
      { label: "Action", key: "action" },
    ],
    []
  );

  const showPopup = (type, title, message, duration = 3000) => {
    setPopup({ visible: true, type, title, message });
    setTimeout(() => {
      setPopup({ visible: false, type: "", title: "", message: "" });
    }, duration);
  };

  const fetchProjectTypes = useCallback(
    async (page = 1) => {
      try {
        setLoading(true);
        const response = await getApi({
          apiUrl: API_URLS.ALL_PROJECT_TYPES(page, pageSize, searchTerm),
        });

        if (response.data.data) {
          setProjectTypes(response.data.data);
          setTotal(response.data.metadata?.total || response.data.data.length);
        } else {
          setProjectTypes([]);
          setTotal(0);
        }
      } catch (error) {
        console.error("Error fetching project types:", error);
        setProjectTypes([]);
      } finally {
        setLoading(false);
      }
    },
    [searchTerm]
  );

  useEffect(() => {
    fetchProjectTypes(pageNo);
  }, [fetchProjectTypes, pageNo]);

  const handleDeleteProjectType = async (id) => {
    try {
      await deleteApi({ apiUrl: API_URLS.DELETE_PROJECT_TYPE(id) });
      showPopup(
        "success",
        "Deleted Successfully",
        "Project Type deleted Successfully"
      );
      fetchProjectTypes(pageNo);
    } catch (error) {
      console.error("Error deleting project type:", error);
      showPopup(
        "error",
        "Error",
        error.message || "Failed to delete Project Type"
      );
    }
  };

  const handleStatusToggle = async (id, checked) => {
    setLoadingProjectTypeIds((prev) => [...prev, id]);
    try {
      const targetItem = projectTypes.find((p) => p._id === id);
      const project_type = targetItem ? targetItem.project_type : "";
      const description = targetItem ? targetItem.description : "";

      const response = await putApi({
        apiUrl: API_URLS.EDIT_PROJECT_TYPE(id),
        data: {
          project_type,
          description,
          isActive: checked,
          status: checked ? "Active" : "Inactive",
        },
      });

      if (response.success) {
        showPopup(
          "success",
          "Status Updated",
          `Project Services has been ${checked ? "activated" : "deactivated"} successfully`
        );
        fetchProjectTypes(pageNo);
      }
    } catch (error) {
      console.error("Error updating project type status:", error);
      showPopup(
        "error",
        "Failed",
        error.message || "Failed to update status"
      );
    } finally {
      setLoadingProjectTypeIds((prev) => prev.filter((item) => item !== id));
    }
  };

  const handleSort = (key, order) => {
    setSortBy(key);
    setSortOrder(order);
  };

  const sortedRawData = useMemo(() => {
    let data = [...projectTypes];
    if (sortBy && sortOrder) {
      data = SortData({ data, key: sortBy, order: sortOrder });
    }
    return data;
  }, [projectTypes, sortBy, sortOrder]);

  const formattedData = useMemo(() => {
    return sortedRawData.map((item, index) => ({
      key: item._id,
      serial: (pageNo - 1) * pageSize + (index + 1),
      project_type: item.project_type,
      description: item.description,
      service_cost: item.service_cost !== undefined ? `₹${item.service_cost}` : "₹0",
      action: (
        <div className="flex gap-4 items-center justify-center">
          <EditIcon
            style={{ cursor: "pointer" }}
            onClick={() => {
              setSelectedProjectType(item);
              setOpenEditDialog(true);
            }}
          />
          <DeleteIcon
            style={{ fontSize: "18px", cursor: "pointer" }}
            onClick={() => handleDeleteProjectType(item._id)}
          />
          <CustomSwitch
            checked={item.isActive !== undefined ? item.isActive : item.status === "Active"}
            disabled={loadingProjectTypeIds.includes(item._id)}
            onChange={(checked) => handleStatusToggle(item._id, checked)}
          />
        </div>
      ),
    }));
  }, [sortedRawData, pageNo, pageSize, loadingProjectTypeIds]);

  return (
    <div
      style={{ padding: "40px", backgroundColor: "#fff" }}
      className="h-screen"
    >
      <div className="flex justify-end text-xs font-light p-3">
        <p>Home</p>
        <span>/</span>
        <TextStyles color="gradient">Project Services</TextStyles>
      </div>
      <div>
        <div className="flex items-center mb-6 gap-2 text-xl text-[#06513D] font-bold px-2">
          {/* <Back onClick={() => navigate(-1)} className="cursor-pointer" /> */}
          <div>Project Services</div>
        </div>

        <div
          style={{
            border: "1px solid #3BA2B959",
            borderRadius: "10px",
            paddingTop: "20px",
            boxShadow: "0px 0px 16px 0px #3BA2B91F",
          }}
        >
          <div className="flex justify-between px-3 mb-3">
            <div className="flex gap-4 pl-9">
              <SearchBar width="300px" onSearch={(value)=>{setSearchTerm(value); setPageNo(1)}} />
            </div>
            <div className="flex gap-4">
              <CustomButton
                buttonType="button"
                variant="ok"
                buttonText=" + Add New"
                onClick={() => setOpenCreateDialog(true)}
                className="text-base font-normal h-[50px] w-[160px]"
              />
            </div>
          </div>

          <Tables
            data={formattedData}
            headers={headers}
            pageSize={pageSize}
            current={pageNo}
            total={total}
            loading={loading}
            onChange={(page) => setPageNo(page)}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
          />
        </div>

        {openCreateDialog && (
          <CreateProjectTypeDialog
            onOpen={openCreateDialog}
            onClose={() => setOpenCreateDialog(false)}
            onSuccess={() => {
              fetchProjectTypes(pageNo);
              setOpenCreateDialog(false);
            }}
            onSave={() => {
              showPopup(
                "success",
                "Created",
                "Project Type Created Successfully"
              );
            }}
            onError={(message) => {
              showPopup(
                "error",
                "Failed",
                message || "Failed to create Project Type"
              );
            }}
          />
        )}

        {openEditDialog && selectedProjectType && (
          <EditProjectTypeDialog
            onOpen={openEditDialog}
            onClose={() => setOpenEditDialog(false)}
            projectData={selectedProjectType}
            onUpdate={() => {
              showPopup(
                "success",
                "Edited",
                "Project Type Edited Successfully"
              );
              fetchProjectTypes(pageNo);
            }}
            onError={() => {
              showPopup("error", "Failed", "Failed in Updating Project Type");
            }}
          />
        )}

        {/* {addNewProjectTypeSuccess && (
          <SuccessDailogBox
            onOpen={addNewProjectTypeSuccess}
            onClose={() => setAddNewProjectTypeSuccess(false)}
            popuptype="success"
            successMessage={"Project Type Created Successfully"}
          />
        )} */}

        {popup.visible && (
          <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center">
            <PopupCard
              type={popup.type}
              title={popup.title}
              message={popup.message}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectType;
